/**
 * Use this file only to keep shadcn's utility functions. There's currently
 * a bug where the value declared at `components.json` -> { "aliases": { "utils": "...", } }
 * is not being intepreted correctly by shadcn cli. So, we have to keep this file here.
 * This should be a temporary issue until the lib fixes it.
 */
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { TENANT_ID_HEADER } from "~/constants";
import { env } from "~/env";
import { authService } from "~/lib/auth-service";
import { CSRF_TOKEN_COOKIE, getCookie } from "./cookie";
import { getCurrentTenantId } from "./tenant-context";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getStableId = () => {
  try {
    const localStorageObject = JSON.parse(JSON.stringify(localStorage));

    const statsigSessionIds = [];
    for (const key of Object.keys(localStorageObject)) {
      if (key.startsWith("statsig.stable_id")) {
        statsigSessionIds.push(localStorageObject[key]);
      }
    }
    // Return the last value, because it's the most recent one
    return statsigSessionIds[statsigSessionIds.length - 1];
  } catch (error) {
    console.error("Error getting stable id:", error);
    return undefined;
  }
};

export const getRequestHeaders = async () => {
  const headers: Record<string, string> = {};

  // Don't send Statsig stable ID for FedRAMP compliance (user tracking identifier)
  if (!env.VITE_IS_FEDRAMP) {
    const stableId = getStableId();
    if (stableId !== undefined && stableId !== "") {
      headers["x-statsig-stable-id"] = stableId;
    }
  }

  const csrfToken = getCookie(CSRF_TOKEN_COOKIE);
  if (csrfToken) {
    headers["X-CSRFToken"] = csrfToken;
  }

  // Get tenant ID from context (set by tenant-context-middleware)
  // This ensures API requests during navigation transitions use the correct tenant
  const tenantId = getCurrentTenantId();
  if (tenantId) {
    headers[TENANT_ID_HEADER] = tenantId;
  }

  // In QA mode, the qa-auth-token cookie is sent automatically by the browser
  // so we don't need to add an Authorization header
  if (!authService.isQaMode()) {
    const accessToken = await authService.getAccessToken();
    if (!accessToken) {
      authService.redirectToLogin();
      return;
    }
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  if (Object.keys(headers).length === 0) {
    return null;
  }
  return headers;
};
